import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type InstitutionMedia = {
  kind: "image" | "video";
  url: string;
  posicao?: number;
};

export type InstitutionFaq = {
  por_que_arrecadando?: string;
  como_recursos_usados?: string;
  prazo_campanha?: string;
  como_ajudar_alem?: string;
  prestacao_contas?: string;
};

export type InstitutionSeo = {
  titulo?: string;
  descricao?: string;
  imagem_compartilhamento?: string;
};

export type InstitutionAddress = {
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  pais?: string;
};

export interface InstitutionWithFullStats {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  slug: string | null;
  total_donations: number | null;
  supporters_count: number;
  is_active: boolean | null;
  cover_url: string | null;
  website: string | null;
  phone: string | null;
  cnpj: string | null;
  city: string | null;
  state: string | null;
  address: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  // New fields (pt-BR payload)
  mission?: string | null;
  vision?: string | null;
  values?: string | null;
  categories?: string[] | null;
  category?: string | null;
  social_media?: Record<string, string> | null;
  address_data?: InstitutionAddress | null;
  seo?: InstitutionSeo | null;
  faq?: InstitutionFaq | null;
  media?: InstitutionMedia[] | null;
  published_at?: string | null;
}

/**
 * Opções compartilhadas: usadas pelo hook no cliente e pelo loader da rota no
 * servidor (ensureQueryData), garantindo a mesma chave de cache e SSR populado.
 */
export const institutionWithStatsQueryOptions = (slug: string) => ({
  queryKey: ["institution", "stats", slug] as const,
  queryFn: async () => {
      // Fetch full institution row (all columns, including the new pt-BR fields)
      const { data: inst, error: slugError } = await supabase
        .from("institutions")
        .select("*")
        .eq("slug", slug)
        .eq("is_active", true)
        .maybeSingle();

      if (slugError) throw slugError;
      if (!inst) return null;

      // Get stats via SECURITY DEFINER RPC (supporters_count etc.)
      const { data: statsRows, error: statsError } = await supabase.rpc(
        "get_institution_with_stats",
        { p_institution_id: inst.id },
      );

      if (statsError) throw statsError;
      const stats = (statsRows?.[0] as Partial<InstitutionWithFullStats>) || {};

      // Merge: stats fills supporters_count / total_donations; full row brings new jsonb fields
      return {
        ...inst,
        supporters_count: stats.supporters_count ?? 0,
        total_donations: stats.total_donations ?? inst.total_donations ?? 0,
      } as unknown as InstitutionWithFullStats;
  },
  retry: 2,
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
});

export const useInstitutionWithStatsBySlug = (slug: string) => {
  return useQuery({
    ...institutionWithStatsQueryOptions(slug),
    enabled: !!slug,
    placeholderData: keepPreviousData,
  });
};
